import { Shape } from '@/services/shape';
import {
  addResourceIfNotExists,
  generateLayoutAst
} from '../utils/resource.utils';
import { TextAttribute } from '@/services/shape/attributes/text';
import { DataSourceFormat } from '@/resources/enum';
import { toArgb, toRgb } from '@/watch-renderer/utils/color';
import { dataSourceWholeItems } from '@/resources/data-source';

export const getTextAst = (resourceAst: Ast, shape: Shape) => {
  const attrs = shape.attrs as TextAttribute;
  const {
    sizeContent,
    rotation,
    fontId = 'misans',
    fontSize,
    fontWeight = 'normal',
    color = '#ffffff',
    style,
    align,
    letterSpace,
    w = 0,
    h = 0,
    radius = 0,
    startAngle = 0,
    verticalAlign,
    strings = [],
    reverseText,
    arcSpan = 45,
    longMode = 'dot'
  } = attrs;

  const refAst: Ast = {
    tag: 'DataItemText',
    attrsMap: {
      w: !sizeContent ? w + '' : '1023',
      h: !sizeContent ? h + '' : '1023',
      rotation: rotation + '',
      fontId: fontId.toLowerCase(),
      fontSize: String(fontSize),
      fontWeight: fontWeight.toLowerCase(),
      color: toArgb(color),
      style: style,
      align:
        style == 'arc'
          ? align ?? 'left'
          : sizeContent
          ? 'left'
          : align ?? 'left',
      letterSpace: String(letterSpace),
      longMode: longMode
    },
    children: []
  };

  // 添加绑定的数据源
  const colors = [
    ...new Set(strings.map((item) => toRgb(item.color ?? '#ffffff')))
  ];
  const isMuiltiColor = colors.length > 1;

  let stringWhole = '';
  let _stringWhole = '';
  strings.forEach((item, index) => {
    const { isSource, value = '', color = '#ffffff', sourceFormat } = item;
    if (index == 0 && value == ' ') return;

    let string: undefined | string;
    // 非数据源
    if (!isSource) {
      string = value.replace('%', '%%');
    } else {
      // 数据源
      const sourceKey = value;
      const sourceItem = dataSourceWholeItems.find(
        (item) => item.key == sourceKey
      );
      if (!sourceItem) return;

      // 添加数据源key
      const _addSouceContent = () => {
        const _sourceKeys: string[] = [];
        if (
          [
            'weatherCurrentSunRise',
            'weatherCurrentSunSet',
            'appAlarm',
          ].includes(sourceKey)
        ) {
          // 时间类数据源：分为小时和分钟拼接
          _sourceKeys.push(...[sourceKey + 'Hour', sourceKey + 'Minute']);
        } else if (['time','dualtime'].includes(sourceKey)) {
          const hasSeconds =
            sourceFormat == DataSourceFormat['Format_8:00:00'] ||
            sourceFormat == DataSourceFormat['Format_08:00:00'];
          // 时间类数据源：分为小时和分钟和秒拼接
          if (hasSeconds) {
            _sourceKeys.push(...[sourceKey + 'Hour', sourceKey + 'Minute', sourceKey + 'Second']);
          } else {
            _sourceKeys.push(...[sourceKey + 'Hour', sourceKey + 'Minute']);
          }
        }else if (['date'].includes(sourceKey)) {
          // 日期类 添加月和日
          _sourceKeys.push(...[sourceKey + 'Month', sourceKey + 'Day']);
        } else if (
          [
            'weatherCurrentTemperatureFahrenheit_temp',
            'weatherTodayTemperatureMaxFahrenheit_temp',
            'weatherTodayTemperatureMinFahrenheit_temp'
          ].includes(sourceKey)
        ) {
          _sourceKeys.push(sourceKey.replace('_temp', ''));
        } else {
          _sourceKeys.push(sourceKey);
        }

        _sourceKeys.forEach((_sourceKey) => {
          refAst.children.push({
            tag: 'Content',
            attrsMap: {
              source: _sourceKey
            },
            children: []
          });
        });
      };
      _addSouceContent();

      // 获取数据源字符串
      const _getSourceString = () => {
        let unit = sourceItem.unit;
        if (unit) unit = unit.replace('%', '%%');
        let string = '%d';
        let format = sourceFormat;
        if (format == undefined || !sourceItem?.formats?.length) {
          format = sourceItem?.precision
            ? DataSourceFormat['Format_8.0']
            : DataSourceFormat.Format_8;
        }

        const isString = sourceKey?.toLowerCase().includes('string');
        if (isString) {
          // 字符串类型
          switch (format) {
            case DataSourceFormat.Format_甲子年:
              string = '%s年';
              break;
            default:
              string = '%s';
          }
        } else {
          switch (format) {
            case DataSourceFormat.Format_8:
              string = '%d';
              break;
            case DataSourceFormat['Format_8.0']:
              string = '%.1f';
              break;
            case DataSourceFormat.Format_08:
              string = '%02d';
              break;
            case DataSourceFormat.Format_8时:
              string = `%d${unit ?? ''}`;
              break;
            case DataSourceFormat['Format_8.0时']:
              string = `%.1f${unit ?? ''}`;
              break;
            case DataSourceFormat.Format_08时:
              string = `%02d${unit ?? ''}`;
              break;
            case DataSourceFormat.Format_SPF8:
              string = `${unit ?? ''}%d`;
              break;
            case DataSourceFormat['Format_SPF8.0']:
              string = `${unit ?? ''}%.1f`;
              break;
            case DataSourceFormat['Format_08:00']:
              string = `%02d:%02d`;
              break;
            case DataSourceFormat['Format_8:00:00']:
              string = `%d:%02d:%02d`;
              break;
            case DataSourceFormat['Format_08:00:00']:
              string = `%02d:%02d:%02d`;
              break;
            case DataSourceFormat['Format_8:00']:
              string = `%d:%02d`;
              break;
            case DataSourceFormat['Format_8/16']:
              string = `%d/%02d`;
              break;
            case DataSourceFormat['Format_08/16']:
              string = `%02d/%02d`;
              break;
            case DataSourceFormat.Format_8月16日:
              string = `%d月%02d日`;
              break;
            case DataSourceFormat.Format_08月16日:
              string = `%02d月%02d日`;
              break;
            case DataSourceFormat['Format_text_8/16']:
              string = `%d/%d`;
              break;
            case DataSourceFormat.Format_text_8月16日:
              string = `%d月%d日`;
              break;
          }
        }

        return string;
      };
      string = _getSourceString();
    }
    if (string) {
      const _color = toRgb(color);
      stringWhole += isMuiltiColor ? `${_color} ${string}#` : string;
      // 编辑器内部使用此数据
      _stringWhole += `${_color} ${string}#`;
    }
  });

  refAst.attrsMap.string = stringWhole;
  refAst.attrsMap._string = _stringWhole;
  if (!isMuiltiColor) refAst.attrsMap.color = colors[0] ?? '#ffffff';

  // 弧形文本独有属性
  if (style === 'arc') {
    const _reverseText = reverseText ? -1 : 1;
    refAst.attrsMap.radius = String(radius);

    // 起始角度
    refAst.attrsMap.startAngle = String(Number(startAngle) * _reverseText);
    // refAst.attrsMap.startAngle = String(startAngle);

    // 修改span值为用户设置
    refAst.attrsMap.span = String(Number(arcSpan) * _reverseText);

    // 垂直对齐属性，需要在此增加判断，因为部分表盘不支持此字段
    if (verticalAlign) {
      refAst.attrsMap.verticalAlign = verticalAlign;
    }
  }

  const ast = addResourceIfNotExists(resourceAst, refAst);
  // 生成 layout ast
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + ast.attrsMap.name,
    x: attrs.x,
    y: attrs.y
  });

  return layoutAst;
};
